import React, { useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import { PaymentElement } from "@stripe/react-stripe-js";
import { usePay } from "../hooks/usePay";
import { MinorUserContext } from "../context/UserNameProvider";
import Question from "../components/Question";
import ContinueButton from "../components/ContinueButton";
import { continueButtonSx } from "../lib/uiformatting";
import { useDispatch, useSelector } from "react-redux";
import { setFiredPurchaseEvent } from "../redux/stripeSlice";
import setPageAndDeferNavigate from "../lib/setPageAndDeferNavigate";

export default function SelfPay() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const { returning = false } = location.state || {};
  const firedPurchaseEvent = useSelector((state) => state.stripe.firedPurchaseEvent);
  const nextLink = returning ? "/anything-else-toknow" : "/photos";
  const { isMinor, firstName } = useContext(MinorUserContext);

  const isUserDataAvailable = user && (user.firstName || user.lastName || user.email || user.phoneNo);

  const [handlePay, message, isLoading, paid, payOptions, isFormComplete] = usePay(nextLink);

  const getSubtitleMessage = () => {
    if (returning) {
      return "Returning patients have a low consult cost of just $79 for the dermatologist's review and any needed medication or treatment plan adjustments.";
    } else {
      const patientReference = isMinor ? `${firstName}'s` : "your";
      const patientRef = isMinor ? `${firstName}` : "you";
      return `For $119, ${patientReference} first visit with Piction Health includes a virtual consultation, a custom treatment plan made just for ${patientRef}, and continued care team support.`;
    }
  };

  const subtitleMessage = getSubtitleMessage();

  if (!isUserDataAvailable) {
    return (
      <Question
        returning={returning}
        title="Loading payment information..."
        subtitle="Please wait while we prepare your payment details."
        input={
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%", py: 4 }}>
            <CircularProgress size={40} />
            <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
              Initializing secure payment...
            </Typography>
          </Box>
        }
      />
    );
  }

  const handleBack = () => {
    if (returning) {
      setPageAndDeferNavigate(dispatch, '/insurance-options', () =>
        navigate('/insurance-options', { state: { returning: true } })
      );
    } else {
      setPageAndDeferNavigate(dispatch, '/allergies', navigate);
    }
  };

  return (
    <Question
      onBack={handleBack}
      returning={returning}
      title={returning ? "The consult is a simple, low fee of $79" : `Confirm ${isMinor ? firstName + "'s" : "your"} payment method.`}
      subtitle={paid ? "Your payment was successful." : subtitleMessage}
      input={
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
          {!paid && (
            <Box sx={{ width: "100%", maxWidth: "500px", mb: "2rem" }}>
              <PaymentElement id="payment-element" options={payOptions} />
            </Box>
          )}
        </Box>
      }
      footer={
        <Box
          sx={{
            ...continueButtonSx,
            position: "sticky",
            bottom: 0,
          }}
        >
          {paid ? (
            <ContinueButton
              onClick={() => {
                if (returning) {
                  setPageAndDeferNavigate(dispatch, '/anything-else-toknow', () =>
                    navigate('/anything-else-toknow', { state: { returning: true } })
                  );
                } else {
                  setPageAndDeferNavigate(dispatch, '/photos', navigate);
                }
              }}
              title="Continue"
              selected={paid}
            />
          ) : (
            <ContinueButton
              onClick={() => {
                if (process.env.REACT_APP_OURS_EVENT) {
                  if (firedPurchaseEvent === false) {
                    // eslint-disable-next-line no-undef
                    ours('track', 'Purchase', { value: 119, currency: 'USD' }, {
                      email: user.email,
                      phone_number: user.phoneNo
                    });
                    dispatch(setFiredPurchaseEvent(true));
                  }
                }
                handlePay();
              }
              }
              title="Submit Payment"
              disabled={isLoading || !isFormComplete}
              selected={!isLoading && isFormComplete}
            />
          )}
        </Box>
      }
    />
  );
}
